'use client';
import { FormCheck } from '@license-admin/boldfjcomponents';
import { useDeviceType } from '@license-admin/boldjobs';
import { useCallback, useMemo, useState } from 'react';
import {
  Checkbox,
  DropCatLink,
  DropCatList,
  DropCatListItem,
} from '../filterBtnGroup/styled';

const VIRTUALIZATION_THRESHOLD = 20;
const ROW_HEIGHT = 32;
const MAX_LIST_HEIGHT = 320;
const OVERSCAN_ROWS = 2;

interface Props {
  selectedCategories: Array<string>;
  isFilterFlyer?: boolean;
  slug: string;
  categoryData?: any;
  categoryVal?: any;
  changeHandler: (_slug: string, _category: string, _name: string) => void;
  handleCategoryFilter: (
    _filter: any,
    _categoryId: string,
    _categoryType: 'Parent' | 'Child'
  ) => void;
  parentId?: string;
  name: string;
}

const FilterCategoryLists = (props: Props) => {
  const {
    categoryVal,
    categoryData,
    changeHandler,
    slug,
    name,
    handleCategoryFilter,
    isFilterFlyer,
    parentId,
    selectedCategories,
  } = props;

  const selectedCategorySet = useMemo(
    () => new Set<string>(selectedCategories ?? []),
    [selectedCategories]
  );

  const isNestedCategoryMode = Boolean(categoryVal);

  const flatCategoryEntries = useMemo(() => {
    const entries = Object.entries(categoryData || {});
    if (!selectedCategories?.length) return entries;
    return entries.sort(([a], [b]) => {
      const aSelected = selectedCategorySet.has(a) ? 0 : 1;
      const bSelected = selectedCategorySet.has(b) ? 0 : 1;
      return aSelected - bSelected;
    });
  }, [categoryData, selectedCategories, selectedCategorySet]);

  const nestedCategoryEntries = useMemo(() => {
    const entries = Array.isArray(categoryVal) ? categoryVal : [];
    if (!selectedCategories?.length) return entries;
    return [...entries].sort((a, b) => {
      const aSelected = selectedCategorySet.has(
        JSON.stringify(a.category.id)
      )
        ? 0
        : 1;
      const bSelected = selectedCategorySet.has(
        JSON.stringify(b.category.id)
      )
        ? 0
        : 1;
      return aSelected - bSelected;
    });
  }, [categoryVal, selectedCategories, selectedCategorySet]);

  const rowCount = isNestedCategoryMode
    ? nestedCategoryEntries.length
    : flatCategoryEntries.length;

  const isMobile = useDeviceType() === 'mobile';

  const shouldVirtualize =
    isMobile && (slug === 'jobtitles' || rowCount > VIRTUALIZATION_THRESHOLD);
  const listHeight = Math.min(rowCount * ROW_HEIGHT, MAX_LIST_HEIGHT);
  const [scrollTop, setScrollTop] = useState(0);

  const handleCategoryClick = useCallback(
    (category: string) => {
      changeHandler(slug, category, name);
    },
    [changeHandler, slug, name]
  );

  const handleParentCategoryClick = useCallback(
    (filter: any) => {
      handleCategoryFilter(
        filter,
        JSON.stringify(filter.category.id),
        'Parent'
      );
    },
    [handleCategoryFilter]
  );

  const renderFlatCategoryRow = useCallback(
    (index: number, style: React.CSSProperties) => {
      const [category] = flatCategoryEntries[index];

      return (
        <div style={style}>
          <DropCatListItem as="div">
            <DropCatLink
              isFilterFlyer={isFilterFlyer}
              onClick={() => handleCategoryClick(category)}
            >
              <>
                <FormCheck
                  type="checkbox"
                  checked={
                    selectedCategories && selectedCategories.includes(category)
                  }
                  id={`${category.replaceAll(' ', '-')}-${parentId}`}
                />
                {category}
              </>
            </DropCatLink>
          </DropCatListItem>
        </div>
      );
    },
    [
      flatCategoryEntries,
      handleCategoryClick,
      isFilterFlyer,
      parentId,
      selectedCategories,
    ]
  );

  const renderNestedCategoryRow = useCallback(
    (index: number, style: React.CSSProperties) => {
      const filter = nestedCategoryEntries[index];

      return (
        <div style={style}>
          <DropCatListItem as="div">
            <DropCatLink
              isFilterFlyer={isFilterFlyer}
              onClick={() => handleParentCategoryClick(filter)}
            >
              <Checkbox
                type="checkbox"
                defaultChecked={
                  selectedCategories &&
                  selectedCategories.includes(
                    JSON.stringify(filter.category.id)
                  )
                }
                id={`checkbox-${filter.category.name.replaceAll(
                  ' ',
                  '-'
                )}-${parentId}`}
              />
              {filter.category.name}
            </DropCatLink>
          </DropCatListItem>
        </div>
      );
    },
    [
      handleParentCategoryClick,
      nestedCategoryEntries,
      isFilterFlyer,
      parentId,
      selectedCategories,
    ]
  );

  const startIndex = Math.max(
    0,
    Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN_ROWS
  );
  const visibleRowCount =
    Math.ceil(listHeight / ROW_HEIGHT) + OVERSCAN_ROWS * 2;
  const endIndex = Math.min(rowCount, startIndex + visibleRowCount);

  if (shouldVirtualize) {
    return (
      <DropCatList>
        <div
          style={{ height: listHeight, overflowY: 'auto' }}
          onScroll={(event) => {
            setScrollTop((event.target as HTMLDivElement).scrollTop);
          }}
        >
          <div
            style={{
              height: rowCount * ROW_HEIGHT,
              position: 'relative',
            }}
          >
            {Array.from({ length: endIndex - startIndex }, (_, offset) => {
              const index = startIndex + offset;
              const rowStyle: React.CSSProperties = {
                position: 'absolute',
                top: index * ROW_HEIGHT,
                left: 0,
                right: 0,
                height: ROW_HEIGHT,
              };

              return (
                <div key={`virtual-row-${index}`}>
                  {isNestedCategoryMode
                    ? renderNestedCategoryRow(index, rowStyle)
                    : renderFlatCategoryRow(index, rowStyle)}
                </div>
              );
            })}
          </div>
        </div>
      </DropCatList>
    );
  }

  if (isNestedCategoryMode) {
    return (
      <>
        {nestedCategoryEntries.map((filter: any, index: number) => {
          return (
            <DropCatListItem key={'cat' + index}>
              <DropCatLink
                isFilterFlyer={isFilterFlyer}
                onClick={() => handleParentCategoryClick(filter)}
              >
                <Checkbox
                  type="checkbox"
                  defaultChecked={
                    selectedCategories &&
                    selectedCategories?.includes(
                      JSON.stringify(filter.category.id)
                    )
                  }
                  id={`checkbox-${filter.category.name.replaceAll(
                    ' ',
                    '-'
                  )}-${parentId}`}
                />
                {filter.category.name}
              </DropCatLink>
            </DropCatListItem>
          );
        })}
      </>
    );
  }

  return (
    <DropCatList>
      {flatCategoryEntries.map(([category]) => {
        return (
          <DropCatListItem key={category}>
            <DropCatLink
              isFilterFlyer={isFilterFlyer}
              onClick={() => handleCategoryClick(category)}
            >
              <>
                <FormCheck
                  type="checkbox"
                  checked={
                    selectedCategories && selectedCategories?.includes(category)
                  }
                  id={`${category.replaceAll(' ', '-')}-${parentId}`}
                />
                {category}
              </>
            </DropCatLink>
          </DropCatListItem>
        );
      })}
    </DropCatList>
  );
};

export default FilterCategoryLists;
